import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { IRoomOverviewReportFilters } from "./Filters";
import { useLanguageContext } from "../../../../../contexts/LanguageContext";
import { GovernanceStatusData, IDefaultStatusData, OccupancyStatusData, RoomStatusData, RoomStatusEnum } from "../../../../../data/Enums";
import { useEffect, useMemo, useState } from "react";
import { IReservation, IRoom } from "../../../../../interfaces/IPartnerPms";
import IEnterprise from "../../../../../interfaces/IEnterprise";
import PartnerPmsService from "../../../../../services/PartnerPmsService";
import FormHelper from "../../../../../helpers/FormHelper";
import { Dvr } from "@mui/icons-material";
import DefaultConstants from "../../../../../data/Constants";


const RoomOverviewContent: React.FunctionComponent<IRoomOverviewReportFilters> = (filters) => {
    const { translate } = useLanguageContext();

    const [rooms, setRooms] = useState<IRoom[]>([]);
    const [expectedCheckInWithoutRoom, setExpectedCheckInWithoutRoom] = useState<IReservation[]>([]);

    useEffect(() => {
        let formData = FormHelper.Create(undefined);
        
        Object.entries(filters).forEach(([key, value]) => {
            if (key === 'enterprise') {
                key = 'enterpriseId';
                value = value.id;
            }

            formData = FormHelper.Add(formData, key, value);
        });

        Promise.all([
            PartnerPmsService.ListRoomOverview(formData),
            PartnerPmsService.ListExpectedCheckInWithoutRoom(formData)
        ]).then(resp => {
            setRooms(resp[0].data.rooms as IRoom[]);
            setExpectedCheckInWithoutRoom(resp[1].data.reservations as IReservation[]);
        }).catch(err => {
            setRooms([]);
            setExpectedCheckInWithoutRoom([]);
        })
        // eslint-disable-next-line
    }, []);


    const renderFilterFieldset = useMemo(() => {
        return <TableCell className='reportpreview-paper-content-fieldset'>
                <Typography variant='caption' className='reportpreview-paper-content-fieldset-label'>
                    {translate('filters')}
                </Typography>

                <Grid container>
                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('occupancyStatus')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.occupancyStatus.length === 0 ? (
                                    translate('all')
                                ) : (
                                    Object.values(OccupancyStatusData)
                                        .filter(s => filters.occupancyStatus.includes(s.key))
                                        .map(s => translate(s.name))
                                        .join(', ')
                                )
                            }
                        </Typography>
                    </Grid>

                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('governanceStatus')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.governanceStatus.length === 0 ? (
                                    translate('all')
                                ) : (
                                    Object.values(GovernanceStatusData)
                                        .filter(s => filters.governanceStatus.includes(s.key))
                                        .map(s => translate(s.name))
                                        .join(', ')
                                )
                            }
                        </Typography>
                    </Grid>

                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('roomStatus')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.roomStatus.length === 0 ? (
                                    translate('all')
                                ) : (
                                    Object.values(RoomStatusData)
                                        .filter(s => filters.roomStatus.includes(s.key))
                                        .map(s => translate(s.name))
                                        .join(', ')
                                )
                            }
                        </Typography>
                    </Grid>

                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('block')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.block.length === 0 ? (
                                    translate('all')
                                ) : (
                                    filters.block.map(b => b.description).join(', ')
                                )
                            }
                        </Typography>
                    </Grid>

                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('floor')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.floor.length === 0 ? (
                                    translate('all')
                                ) : (
                                    filters.floor.map(f => f.description).join(', ')
                                )
                            }
                        </Typography>
                    </Grid>

                    <Grid xs={3} className='reportpreview-paper-content-fieldset-item'>
                        <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                            {translate('roomType')}
                        </Typography>

                        <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                            {
                                filters.roomType.length === 0 ? (
                                    translate('all')
                                ) : (
                                    filters.roomType.map(r => r.description).join(', ')
                                )
                            }
                        </Typography>
                    </Grid>

                    {
                        (filters.enterprise?.partnerPms.userRooms ?? []).length > 0 && (
                            <Grid xs={6} className='reportpreview-paper-content-fieldset-item'>
                                <Typography variant='subtitle1' className='reportpreview-paper-content-fieldset-item-title'>
                                    {translate('_showOnlyRoomsAssignedToMe')}
                                </Typography>

                                <Typography variant='body1' className='reportpreview-paper-content-fieldset-item-value'>
                                    {translate(filters.onlyUserRooms ? 'yes' : 'no')}
                                </Typography>
                            </Grid>
                        )
                    }
                </Grid>
            </TableCell>;
    }, [filters, translate]);


    const data = useMemo(() => {
        const { userRooms } = (filters.enterprise as IEnterprise).partnerPms;
        let data = [...rooms];

        const hasUserRooms = userRooms.length > 0;
        if (hasUserRooms && filters.onlyUserRooms) {
            const userRoomsIds = userRooms.map(r => r.id);
            data = data.filter(r => userRoomsIds.includes(r.id));
        }

        if (filters.occupancyStatus.length > 0) {
            const occupancyStatus = Object.values(OccupancyStatusData).filter(s => filters.occupancyStatus.includes(s.key)).map(s => s.partnerId ?? '');
            data = data.filter(r => occupancyStatus.includes(r.occupancyStatusId));
        }

        if (filters.governanceStatus.length > 0) {
            const governanceStatus = Object.values(GovernanceStatusData).filter(s => filters.governanceStatus.includes(s.key)).map(s => s.partnerId ?? '');
            data = data.filter(r => governanceStatus.includes(r.governanceStatusId));
        }

        if (filters.roomStatus.length > 0) {
            const roomStatus = Object.values(RoomStatusData).filter(s => filters.roomStatus.includes(s.key)).map(s => s.partnerId ?? '');
            data = data.filter(r => {
                return roomStatus.some(s => {
                    const roomKey = s as keyof(IRoom);
                    const reservationKey = s as keyof(IReservation);
                    return r[roomKey] === true || (r.checkInReservation && r.checkInReservation[reservationKey] === true);
                });
            });
        }

        if (filters.block.length > 0) {
            const block = filters.block.map(b => b.id);
            data = data.filter(r => block.includes(r.blockId));
        }

        if (filters.floor.length > 0) {
            const floor = filters.floor.map(f => f.id);
            data = data.filter(r => floor.includes(r.floorId));
        }

        if (filters.roomType.length > 0) {
            const roomType = filters.roomType.map(r => r.id);
            data = data.filter(r => roomType.includes(r.roomTypeId));
        }

        return data;
        // eslint-disable-next-line
    }, [rooms, filters]);

    
    return <>
        <TableRow>
            {renderFilterFieldset}
        </TableRow>

        <TableRow>
            <TableCell className='border-none' padding='none'>
                {
                    data.length > 0 ? (
                        <>
                            <Table className='data-table' sx={{ marginY: DefaultConstants.gridRowSpacing }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('governanceStatus')}</TableCell>
                                        <TableCell>{translate('description')}</TableCell>
                                        <TableCell>{translate('block')}</TableCell>
                                        <TableCell>{translate('floor')}</TableCell>
                                        <TableCell>{translate('occupancyStatus')}</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                
                                <TableBody className='tbody-striped'>
                                    {
                                        data.map(row => {
                                            const occupancyStatus = Object.values(OccupancyStatusData).find(s => s.partnerId === row.occupancyStatusId) as IDefaultStatusData;
                                            const governanceStatus = Object.values(GovernanceStatusData).find(s => s.partnerId === row.governanceStatusId) as IDefaultStatusData;

                                            return <TableRow key={row.id}>
                                                <TableCell>
                                                    <Typography className='text-title' sx={{ color: `${governanceStatus.color} !important` }}>
                                                        <governanceStatus.Icon />
                                                        {translate(governanceStatus.name)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {translate('room')} {row.description} </Typography>
                                                    <Typography className='text-subtitle'> {row.roomTypeDescription} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {row.blockDescription} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title'> {row.floorDescription} </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    <Typography className='text-title' sx={{ color: `${occupancyStatus.color} !important` }}>
                                                        <occupancyStatus.Icon />
                                                        {translate(occupancyStatus.name)}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell className='display-inlineflex'>
                                                    {
                                                        Object.values(RoomStatusData).map(s => {
                                                            const roomKey = (s.partnerId ?? '') as keyof(IRoom);
                                                            const reservationKey = (s.partnerId ?? '') as keyof(IReservation);
                                    
                                                            if (row[roomKey] === true || (row.checkInReservation && row.checkInReservation[reservationKey] === true)) {
                                                                return <Box className='verticalstatus-box' sx={{ color: `${s.color} !important` }}>
                                                                    <s.Icon />
                                                                    <Typography>{translate(s.name)}</Typography>
                                                                </Box>;
                                                            }
                                    
                                                            return null;
                                                        })
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        })
                                    }
                                </TableBody>
                            </Table>
                        </>
                    ) : (
                        <Box className='reportpreview-paper-content-notfound' marginBottom={DefaultConstants.gridRowSpacing}>
                            <Typography variant='body1'>{translate('_roomOverviewEmptyRoomList')}</Typography>
                        </Box>
                    )
                }

                {/* Summary */}
                <Table className='page-break show-only-in-print'>
                    {renderFilterFieldset}
                </Table>

                <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={3}>{translate('summary')}</TableCell>
                        </TableRow>
                    </TableHead>
                    
                    <TableBody className='tbody-striped'>
                        <TableRow>
                            <TableCell className='color-primary display-flex alignitems-center fontweight-600 gap-10'>
                                <Dvr className='fontsize-20' />
                                {translate('rooms')}
                            </TableCell>
                            <TableCell className='textalign-right fontweight-600'>{data.length}</TableCell>
                            <TableCell className='textalign-right fontweight-600' width={80}>100.0 %</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>

                <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={3}>{translate('occupancyStatus')}</TableCell>
                        </TableRow>
                    </TableHead>
                    
                    <TableBody className='tbody-striped'>
                        {
                            Object.values(OccupancyStatusData).filter(s => s.partnerId).map(s => {
                                const value = data.filter(r => r.occupancyStatusId === s.partnerId).length;
                                const percentage = data.length === 0 ? 0 : value / data.length * 100;

                                return <TableRow>
                                    <TableCell 
                                        className='display-flex alignitems-center fontweight-600 gap-10'
                                        sx={{
                                            color: `${s.color} !important`
                                        }}
                                    >
                                        <s.Icon className='fontsize-20' />
                                        {translate(s.name)}
                                    </TableCell>
                                    <TableCell className='textalign-right fontweight-600'>{value}</TableCell>
                                    <TableCell className='textalign-right fontweight-600' width={80}>{percentage.toFixed(1)} %</TableCell>
                                </TableRow>;
                            })
                        }
                    </TableBody>
                </Table>

                <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={3}>{translate('governanceStatus')}</TableCell>
                        </TableRow>
                    </TableHead>
                    
                    <TableBody className='tbody-striped'>
                        {
                            Object.values(GovernanceStatusData).filter(s => s.partnerId).map(s => {
                                const value = data.filter(r => r.governanceStatusId === s.partnerId).length;
                                const percentage = data.length === 0 ? 0 : value / data.length * 100;

                                return <TableRow>
                                    <TableCell 
                                        className='display-flex alignitems-center fontweight-600 gap-10'
                                        sx={{
                                            color: `${s.color} !important`
                                        }}
                                    >
                                        <s.Icon className='fontsize-20' />
                                        {translate(s.name)}
                                    </TableCell>
                                    <TableCell className='textalign-right fontweight-600'>{value}</TableCell>
                                    <TableCell className='textalign-right fontweight-600' width={80}>{percentage.toFixed(1)} %</TableCell>
                                </TableRow>;
                            })
                        }
                    </TableBody>
                </Table>

                <Table className='data-table' sx={{ marginTop: DefaultConstants.gridRowSpacing }}>
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={3}>{translate('roomStatus')}</TableCell>
                        </TableRow>
                    </TableHead>
                    
                    <TableBody className='tbody-striped'>
                        {
                            Object.values(RoomStatusData).filter(s => s.partnerId).map(s => {
                                const roomKey = (s.partnerId ?? '') as keyof(IRoom);
                                const reservationKey = (s.partnerId ?? '') as keyof(IReservation);
                                
                                let value = data.filter(r => r[roomKey] === true || (r.checkInReservation && r.checkInReservation[reservationKey] === true)).length;
                                let percentage = data.length === 0 ? 0 : value / data.length * 100;

                                const rows = [] as JSX.Element[];
                                rows.push(<TableRow>
                                    <TableCell 
                                        className='display-flex alignitems-center fontweight-600 gap-10'
                                        sx={{
                                            color: `${s.color} !important`
                                        }}
                                    >
                                        <s.Icon className='fontsize-20' />
                                        {translate(s.name)}
                                    </TableCell>
                                    <TableCell className='textalign-right fontweight-600'>{value}</TableCell>
                                    <TableCell className='textalign-right fontweight-600' width={80}>{percentage.toFixed(1)} %</TableCell>
                                </TableRow>);


                                if (s.key === RoomStatusEnum.ExpectedCheckIn) {
                                    value = expectedCheckInWithoutRoom.length ?? 0;
                                    percentage = data.length === 0 ? 0 : value / data.length * 100;

                                    rows.push(<TableRow>
                                        <TableCell 
                                            className='display-flex alignitems-center fontweight-600 gap-10'
                                            sx={{
                                                color: `${s.color} !important`
                                            }}
                                        >
                                            <s.Icon className='fontsize-20' />
                                            {translate('expectedCheckInWithoutRoom')}
                                        </TableCell>
                                        <TableCell className='textalign-right fontweight-600'>{value}</TableCell>
                                        <TableCell className='textalign-right fontweight-600' width={80}>{percentage.toFixed(1)} %</TableCell>
                                    </TableRow>);
                                }

                                return rows;
                            })
                        }
                    </TableBody>
                </Table>
            </TableCell>
        </TableRow>
    </>;
}

export default RoomOverviewContent;